import { forwardRef } from 'react';
import { cn } from '../lib/utils';

/**
 * SmartImage — a wrapper around <img> that adds:
 *  - lazy loading (unless `priority`)
 *  - async decoding + fetchpriority
 *  - responsive srcset for Unsplash-hosted images
 *  - explicit width/height for layout stability
 *  - a subtle blur-up placeholder via CSS background
 */
const SmartImage = forwardRef(function SmartImage(
  {
    src,
    alt = '',
    width = 800,
    height = 1067, // 3:4 aspect default
    sizes = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
    priority = false,
    className = '',
    imgClassName = '',
    aspect = '3/4',
    ...rest
  },
  ref
) {
  const srcSet = buildSrcSet(src);
  return (
    <div
      className={cn('relative overflow-hidden bg-muted', className)}
      style={{ aspectRatio: aspect }}
    >
      {/* Blur placeholder — a solid muted panel that fades when the img is loaded */}
      <div aria-hidden className="absolute inset-0 bg-muted animate-pulse opacity-50" />
      <img
        ref={ref}
        src={src}
        srcSet={srcSet || undefined}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchpriority={priority ? 'high' : 'auto'}
        className={cn('relative h-full w-full object-cover transition-opacity duration-500', imgClassName)}
        onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
        style={{ opacity: 0 }}
        {...rest}
      />
    </div>
  );
});

// Build an Unsplash srcset by swapping the ?w= query param
function buildSrcSet(src) {
  if (!src || !src.includes('images.unsplash.com')) return '';
  const widths = [400, 600, 800, 1000, 1200, 1600];
  return widths.map((w) => `${swapW(src, w)} ${w}w`).join(', ');
}
function swapW(src, w) {
  if (src.includes('w=')) return src.replace(/w=\d+/, `w=${w}`);
  return src + (src.includes('?') ? '&' : '?') + `w=${w}`;
}

export default SmartImage;
